import React, { FunctionComponent, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import { trackEvent } from '@companion-professional/checkup/src/lib/trackEvent';
import { useIsLoggedIn, usePasswordNeedsReset } from './state/auth';
import { LoggedInRoutes } from './LoggedInRoutes';
import { Login, LoginPasswordChange } from './pages';
import { useSetPosthogIdentify } from './hooks/useSetPosthogIdentify';
import { WindowWithVportalSettings } from './types/global';

declare let window: WindowWithVportalSettings;

// App is the root component of the application. It is responsible for ensuring the user is logged in and rendering the
// appropriate content based on the logged in state.
export const App: FunctionComponent = () => {
  const location = useLocation();
  const posthog = usePostHog();
  const isLoggedIn = useIsLoggedIn();
  const passwordNeedsReset = usePasswordNeedsReset();

  // The following is to handle routing from when this app was using hash routing.  The app is now using browser
  // routing, but we need to handle the case where a link was shared/sent/bookmarked with a hash in the URL.
  const navigate = useNavigate();
  useEffect(() => {
    if (location.hash.startsWith('#/')) {
      // Report the redirect event to PostHog so we can see how many people are still using hash routing.
      const realPath = location.hash.replace('#', '');
      trackEvent('hashroute_redirect', {
        from: location.hash,
        to: realPath
      });
      navigate(realPath);
    }
  }, []);

  // Because this is a Single Page App, the is needed to track pageviews in PostHog (as explained in this tutorial:
  // https://posthog.com/tutorials/single-page-app-pageviews)
  useEffect(() => {
    posthog.capture('$pageview');
  }, [location]);

  // Custom hook that sets the PostHog identify context for the user.
  useSetPosthogIdentify();

  if (!isLoggedIn) {
    return <Login />;
  }

  // If the user is logged in, but their password needs to be reset; show the password change form
  if (passwordNeedsReset) {
    return <LoginPasswordChange />;
  }

  return <LoggedInRoutes />;
};
